/**
 * `ChatLevelDialog` — the level pick POPOVER (operator ruling 2026-08-29,
 * miniplan §2-bis.a: anchored above the composer like other chatbots' model
 * pickers, NOT a modal; house idiom = `MentionPicker`'s bottom-full panel).
 *
 * Each row: mode icon · label (bold) · one-line explanation (operator copy,
 * §2-bis.b) · the org's REAL resolved ref in small print (server-resolved —
 * the SPA never derives it; rows come from `lx.get_chat_levels` and are
 * INFORMATIONAL, contract AC-6) · checkmark on the current selection.
 *
 * Picking applies immediately; every level SETS (⚖ R-C, contract v1.6 §4 —
 * NORMAL is a real set, revising the v1.4 NORMAL≡clear mechanic/B-v4; the
 * FAST default needs no clear affordance). A retryable failure keeps the
 * popover open + shows the retry line; a definitive one closes into the L-9
 * gate (owned by the chip's parent).
 */
import { Check, Hourglass, Rocket, Scale } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ChatLevelRow } from "@/hooks/useChatLevel";
import {
  CHAT_LEVELS,
  chipFullRef,
  levelExplanationKey,
  levelLabelKey,
  profileNameThinkingSuffix,
  type ChatLevelUiState,
  type ConciergeMode,
} from "@/lib/chat-level";

const MODE_ICONS: Record<ConciergeMode, typeof Rocket> = {
  FAST: Rocket,
  NORMAL: Scale,
  DEEP: Hourglass,
};

interface ChatLevelDialogProps {
  state: ChatLevelUiState;
  rows: ChatLevelRow[] | null;
  busy: boolean;
  /** Where the ORG DEFAULT lands (unique model match) — the check marks it
   *  when no session pick exists (iteration 3). Null when none/ambiguous. */
  defaultMatch: ConciergeMode | null;
  /** Applies the pick; resolves `false` on the retryable class. */
  onPick: (level: ConciergeMode) => Promise<boolean>;
  onClose: () => void;
  /** ⚖ v1.7 §D (ii): the seam verified the upstream patch and it did NOT
   *  apply, so it refused to write the store. Distinct from the retryable
   *  class (the call answered fine) and from L-9 (the catalog is not
   *  broken) — the pick simply did not take effect. */
  notApplied?: boolean;
}

export function ChatLevelDialog({
  state,
  rows,
  busy,
  defaultMatch,
  onPick,
  onClose,
  notApplied = false,
}: ChatLevelDialogProps) {
  const { t } = useTranslation();
  const [retryFailed, setRetryFailed] = useState(false);

  // Operator ruling (2026-08-29, iteration 3): the CHECK icon marks the
  // current setting — a real session pick, or where the org default lands
  // (Claude-picker idiom). The read-back stays honest underneath; only the
  // marker is unified.
  const current: ConciergeMode | null =
    state.kind === "session-level" ? state.level : defaultMatch;

  // go-live prod finding 2026-08-31 (-1re7), defect 1: when the org default
  // corresponds to NO level (e.g. prod's LOW:low), three unmarked options are
  // indistinguishable from a rendering bug. The off-level current state must
  // be REPRESENTABLE: name what is actually active, in the dialog itself.
  const offLevelRef =
    state.kind === "org-default" && current === null
      ? chipFullRef(state.resolved, profileNameThinkingSuffix(state.profileName), null)
      : null;

  const rowFor = (level: ConciergeMode): ChatLevelRow | undefined =>
    rows?.find((r) => r.level === level);

  return (
    <>
      {/* click-away backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      <div
        role="listbox"
        data-testid="chat-level-dialog"
        aria-label={t("chat.level.pickerAria")}
        className="absolute bottom-full right-0 z-50 mb-2 w-72 overflow-hidden rounded-lg border border-cream-200/80 bg-white py-1 shadow-[0_8px_24px_rgba(28,24,20,0.12)] dark:border-coffee-700/70 dark:bg-coffee-900"
      >
        {CHAT_LEVELS.map((level) => {
          const Icon = MODE_ICONS[level];
          const row = rowFor(level);
          const unavailable = row?.unavailableReason != null;
          const ref = row?.resolved
            ? `${row.resolved.provider}/${row.resolved.model}${
                row.resolved.thinking ? ` · ${row.resolved.thinking}` : ""
              }`
            : null;
          return (
            <button
              key={level}
              type="button"
              role="option"
              aria-selected={current === level}
              data-testid={`chat-level-option-${level.toLowerCase()}`}
              disabled={busy || unavailable}
              onClick={() => {
                setRetryFailed(false);
                void onPick(level).then((ok) => {
                  if (!ok) setRetryFailed(true);
                });
              }}
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-cream-100/60 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-coffee-800/60"
            >
              {/* go-live -1re7 defect 2: was text-coffee-500/dark:text-cream-500/70 —
                  near-invisible on the dark panel; now the checkmark's proven pair. */}
              <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coffee-600 dark:text-cream-300" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-coffee-800 dark:text-cream-200">
                  {t(levelLabelKey(level))}
                </span>
                <span className="block text-[11px] text-coffee-500 dark:text-cream-500/70">
                  {t(levelExplanationKey(level))}
                </span>
                {/* Full ref lives HERE, not on the chip (§2-bis.c). */}
                {ref && (
                  <span className="mt-0.5 block truncate font-mono text-[10px] text-coffee-500/70 dark:text-cream-500/50">
                    {ref}
                  </span>
                )}
                {unavailable && (
                  <span className="mt-0.5 block text-[10px] text-red-600 dark:text-red-400">
                    {t("chat.level.rowUnavailable")}
                  </span>
                )}
              </span>
              {current === level && (
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coffee-600 dark:text-cream-300" aria-hidden="true" />
              )}
            </button>
          );
        })}
        <div className="border-t border-cream-200/80 px-3 pb-1.5 pt-1.5 dark:border-coffee-700/70">
          {offLevelRef && (
            <p
              data-testid="chat-level-off-levels"
              className="pb-1 text-[10px] leading-snug text-coffee-600 dark:text-cream-300"
            >
              {t("chat.level.currentOffLevels", { ref: offLevelRef })}
            </p>
          )}
          {/* ⚖ v1.7 §D (ii): a VERIFIED non-apply is not a retry situation —
              the call answered honestly and the chip still shows the previous,
              still-true level. Distinct wording, and it wins over the retry
              line so the two can never both claim the same failure. */}
          {notApplied ? (
            <p
              data-testid="chat-level-not-applied"
              className="pb-1 text-[10px] text-red-600 dark:text-red-400"
            >
              {t("chat.level.pinNotApplied")}
            </p>
          ) : (
            retryFailed && (
              <p className="pb-1 text-[10px] text-red-600 dark:text-red-400">
                {t("chat.level.applyFailedRetry")}
              </p>
            )
          )}
          <p className="text-[10px] leading-snug text-coffee-500 dark:text-cream-500/50">
            {t("chat.level.howItWorks")}
          </p>
        </div>
      </div>
    </>
  );
}
